"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

interface UseChatAutoScrollOptions {
  /** The agent turn is still streaming (typing dots or presets loading). */
  isStreaming: boolean;
  /** An image generation/modification is in flight (progress card visible). */
  isGenerating: boolean;
  /** Visible message count; an increase means a message was appended. */
  messageCount: number;
  /** A submitted user turn should bring the conversation back to the latest message. */
  lastMessageRole?: "user" | "assistant";
}

interface UseChatAutoScrollResult {
  /** Attach to the scroll viewport (the overflow-y-auto element). */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Attach to the inner content wrapper the ResizeObserver watches. */
  contentRef: React.RefObject<HTMLDivElement | null>;
}

/** How close to the bottom (px) still counts as "pinned" to the bottom. */
const PINNED_THRESHOLD_PX = 48;

/**
 * Suppresses the momentary isTyping→presetsLoading flap: the settle anchor
 * only fires if no new activity starts within this window.
 */
const SETTLE_DEBOUNCE_MS = 150;

/**
 * Structured action metadata can mount just after streaming becomes idle.
 * Follow those final layout changes briefly, then return to idle behavior so
 * expanding an old brief or loading a font cannot pull the transcript down.
 */
const POST_SETTLE_RESIZE_GRACE_MS = 500;

/** Ignore resize-driven scroll events unless the customer just moved the viewport. */
const USER_SCROLL_INTENT_MS = 500;

/**
 * Chat auto-scroll behavior:
 *
 * - While a turn is streaming or a generation is running, the view stays
 *   pinned to the bottom — but only if the user is already at the bottom.
 *   Scrolling up to reread is respected; we never yank the view back down.
 * - Content growth (streamed sentences, images decoding, preset grids
 *   mounting) is observed via ResizeObserver, so each height settle produces
 *   exactly one scroll decision — no timer guessing.
 * - When the turn settles, if the latest assistant message plus its widgets
 *   is taller than the viewport, anchor to the top of that message instead:
 *   bottom-pinning would scroll the assistant's words out of view and leave
 *   the user staring at buttons with no idea what was said about them.
 */
export function useChatAutoScroll({
  isStreaming,
  isGenerating,
  messageCount,
  lastMessageRole,
}: UseChatAutoScrollOptions): UseChatAutoScrollResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  const isActive = isStreaming || isGenerating;
  const resizeActiveRef = useRef(isActive);
  const postSettleResizeRef = useRef(false);
  const wasActiveRef = useRef(isActive);
  const prevMessageCountRef = useRef(messageCount);
  const lastGeometryRef = useRef<{
    scrollHeight: number;
    clientHeight: number;
  } | null>(null);
  const lastVisibleScrollTopRef = useRef(0);
  const viewportWasHiddenRef = useRef(false);
  const lastUserScrollIntentRef = useRef(0);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const viewport = containerRef.current;
    if (!viewport || viewport.clientHeight <= 0) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
  }, []);

  const positionSettledTurn = useCallback(
    (behavior: ScrollBehavior) => {
      const viewport = containerRef.current;
      if (!viewport || viewport.clientHeight <= 0 || !pinnedRef.current) return;
      const anchors = viewport.querySelectorAll<HTMLElement>(
        '[data-agent-message="assistant"]'
      );
      const latest = anchors[anchors.length - 1];
      if (latest) {
        const viewportRect = viewport.getBoundingClientRect();
        const latestTop =
          latest.getBoundingClientRect().top -
          viewportRect.top +
          viewport.scrollTop;
        const latestTurnHeight = viewport.scrollHeight - latestTop;
        if (latestTurnHeight > viewport.clientHeight) {
          // A tall reply should begin with its explanation rather than landing
          // at the bottom of its action cards. Treat this as an intentional
          // reading position so later layout changes cannot pull it away.
          pinnedRef.current = false;
          viewport.scrollTo({ top: Math.max(latestTop - 8, 0), behavior });
          return;
        }
      }
      scrollToBottom(behavior);
    },
    [scrollToBottom]
  );

  // Track whether the user is at (or near) the bottom of the transcript.
  useEffect(() => {
    const viewport = containerRef.current;
    if (!viewport) return;
    const markUserScrollIntent = () => {
      lastUserScrollIntentRef.current = Date.now();
    };
    const handleScrollKey = (event: KeyboardEvent) => {
      if (
        [
          "ArrowUp",
          "ArrowDown",
          "PageUp",
          "PageDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      ) {
        markUserScrollIntent();
      }
    };
    const handleScroll = () => {
      const geometry = {
        scrollHeight: viewport.scrollHeight,
        clientHeight: viewport.clientHeight,
      };
      const previousGeometry = lastGeometryRef.current;
      lastGeometryRef.current = geometry;
      if (geometry.clientHeight <= 0) {
        viewportWasHiddenRef.current = true;
        return;
      }
      // Content growth, keyboard resize, and a hidden tab can all fire scroll
      // events without customer input. Preserve their previous pin state;
      // only a scroll within unchanged geometry expresses a new position.
      if (
        previousGeometry &&
        (previousGeometry.scrollHeight !== geometry.scrollHeight ||
          previousGeometry.clientHeight !== geometry.clientHeight) &&
        Date.now() - lastUserScrollIntentRef.current > USER_SCROLL_INTENT_MS
      ) {
        return;
      }
      lastVisibleScrollTopRef.current = viewport.scrollTop;
      const distanceFromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      pinnedRef.current = distanceFromBottom < PINNED_THRESHOLD_PX;
    };
    handleScroll();
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    viewport.addEventListener("wheel", markUserScrollIntent, { passive: true });
    viewport.addEventListener("touchmove", markUserScrollIntent, {
      passive: true,
    });
    viewport.addEventListener("pointerdown", markUserScrollIntent, {
      passive: true,
    });
    viewport.addEventListener("keydown", handleScrollKey);
    return () => {
      viewport.removeEventListener("scroll", handleScroll);
      viewport.removeEventListener("wheel", markUserScrollIntent);
      viewport.removeEventListener("touchmove", markUserScrollIntent);
      viewport.removeEventListener("pointerdown", markUserScrollIntent);
      viewport.removeEventListener("keydown", handleScrollKey);
    };
  }, []);

  // Open the transcript at the bottom.
  useLayoutEffect(() => {
    scrollToBottom("auto");
  }, [scrollToBottom]);

  // The final action widget can arrive one render after the streaming flags
  // clear. Keep resize-following alive only for that short handoff window.
  useEffect(() => {
    const wasActive = resizeActiveRef.current;
    resizeActiveRef.current = isActive;

    if (isActive) {
      postSettleResizeRef.current = false;
      return;
    }
    if (!wasActive) return;

    postSettleResizeRef.current = true;
    const timeoutId = window.setTimeout(() => {
      postSettleResizeRef.current = false;
    }, POST_SETTLE_RESIZE_GRACE_MS);
    return () => {
      window.clearTimeout(timeoutId);
      postSettleResizeRef.current = false;
    };
  }, [isActive]);

  // Follow active content growth, plus viewport size changes caused by the
  // mobile keyboard or returning from a hidden Front/Details panel. A hidden
  // viewport has zero height, so scrolling while it is hidden has no effect.
  useEffect(() => {
    const content = contentRef.current;
    const viewport = containerRef.current;
    if (!content || !viewport || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(entries => {
      const viewportResized = entries.some(entry => entry.target === viewport);
      if (viewportResized) {
        if (viewport.clientHeight <= 0) {
          viewportWasHiddenRef.current = true;
        } else if (viewportWasHiddenRef.current) {
          viewportWasHiddenRef.current = false;
          if (pinnedRef.current) {
            if (resizeActiveRef.current) scrollToBottom("auto");
            else positionSettledTurn("auto");
          } else {
            // Some browsers reset scrollTop when a panel uses display:none.
            viewport.scrollTo({
              top: lastVisibleScrollTopRef.current,
              behavior: "auto",
            });
          }
          return;
        } else if (pinnedRef.current) {
          scrollToBottom("auto");
          return;
        }
      }
      if (
        entries.some(entry => entry.target === content) &&
        pinnedRef.current &&
        (resizeActiveRef.current || postSettleResizeRef.current)
      ) {
        scrollToBottom("auto");
      }
    });
    observer.observe(content);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [positionSettledTurn, scrollToBottom]);

  // Sending a new user turn re-pins. A new assistant turn follows only if the
  // customer is already at the bottom; reading earlier messages takes priority.
  useEffect(() => {
    if (messageCount > prevMessageCountRef.current) {
      if (lastMessageRole === "user") pinnedRef.current = true;
      if (pinnedRef.current) scrollToBottom("auto");
    }
    prevMessageCountRef.current = messageCount;
  }, [messageCount, lastMessageRole, scrollToBottom]);

  // Turn settled: anchor tall assistant turns to their top. The anchor is
  // debounced because the streaming flags flap for a beat between "text done"
  // and "presets loading" — anchoring inside that gap would un-pin the view
  // mid-stream. The cleanup cancels the pending anchor if activity resumes.
  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (!wasActive || isActive) return;

    const timeoutId = window.setTimeout(() => {
      const viewport = containerRef.current;
      if (!viewport || viewport.clientHeight <= 0 || !pinnedRef.current) return;
      // Smooth scrolling needs rendering frames; in a hidden tab the
      // animation stalls and never lands, so jump instantly instead.
      const behavior: ScrollBehavior =
        typeof document !== "undefined" && document.visibilityState === "hidden"
          ? "auto"
          : "smooth";
      positionSettledTurn(behavior);
    }, SETTLE_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isActive, positionSettledTurn]);

  return { containerRef, contentRef };
}
